import { AppThunk } from "../app/store";
import {
  searchAsins,
  getEditableAttributes,
  prepareRecordAsinMarketplaceDatesWithAsins,
  recordAsinMarketplaceDatesWithAsins,
  prepareSetLaunchAttributesWithAsins,
  setLaunchAttributesWithAsins,
} from "../app/api";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../from-server/asin-central/model";
import { SearchResult, SortFacet } from "../../from-server/asin-central/search";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthorizedAttribute } from "../../from-server/brass/model";
import {
  porKey,
  RESTRICTED_SEARCH_MESSAGE_ITEMS,
  SET_ASIN_MARKETPLACE_ATTRIBUTES_SUCCESS_ITEMS,
  SET_ASIN_MARKETPLACE_DATES_SUCCESS_ITEMS,
} from "./PorAsinListTable-config";
import {
  convertDateStringToUTCDate,
  getPstOffset,
  getRelativeDate,
} from "../../util/dateUtils";
import {
  addNotificationWithKey,
  clearNotifications,
  removeNotification,
  RESTRICTED_ASINS_NOTIFICATION_KEY,
  setNotification,
} from "../../features/notification/notificationConnector";
import { IN_PROGRESS_NOTIF_ITEMS } from "../notification/notification-config";
import { ERROR_NOTIF_ITEMS } from "../notification/notification-config";
import moment from "moment/moment";

interface PorAsinListState {
  currentPagePorAsins: Array<FullAsinWithAudits | RestrictedAsin>;
  count: number;
  isLoading: boolean;
  error: string | null;
  isEditing: boolean;
  porIndexMap: { [asinMp: string]: number };
  edited: boolean;
  editedRowSet: { [index: number]: boolean };
  editablePorAsins: Array<FullAsinWithAudits | RestrictedAsin>;
  refresh: boolean;
  restrictedPrograms: string[];
  editableAttributes: AuthorizedAttribute[];
  isGettingEditableAttributes: boolean;
  editableAttributesLoaded: boolean;
  lastFetchedQueryString: string | null;
}

const porAsinListInitialState: PorAsinListState = {
  currentPagePorAsins: [],
  count: 0,
  isLoading: false,
  error: null,
  isEditing: false,
  porIndexMap: {},
  edited: false,
  editedRowSet: {},
  editablePorAsins: [],
  refresh: false,
  restrictedPrograms: [],
  editableAttributes: [],
  isGettingEditableAttributes: false,
  editableAttributesLoaded: false,
  lastFetchedQueryString: null,
};

function updateEditedEntry(
  _state: PorAsinListState,
  attribute: "announceDate" | "onlineStreetDate" | "userDescription",
  value: string,
  entryToUpdate: FullAsinWithAudits
): FullAsinWithAudits {
  const updatedEntry = { ...entryToUpdate };
  if (attribute !== "userDescription") {
    // value is expected to have no offset, so we add the PST offset here before updating.
    const dateVal =
      value.trim().length === 0 ? undefined : convertDateStringToUTCDate(value);
    const pstOffSet = dateVal ? getPstOffset(dateVal) : 0;
    const updatedDate = dateVal
      ? getRelativeDate(dateVal, -pstOffSet, "minute")
      : undefined;
    updatedEntry[attribute] = updatedDate;
  } else {
    updatedEntry[attribute] = value.trim();
  }
  return updatedEntry;
}

const porAsinList = createSlice({
  name: "porAsinList",
  initialState: porAsinListInitialState,
  reducers: {
    getEditableAttributesStart(state: PorAsinListState) {
      state.isGettingEditableAttributes = true;
    },
    getEditableAttributesSuccess(state: PorAsinListState, { payload }) {
      state.editableAttributes = payload;
      state.editableAttributesLoaded = true;
    },
    getEditableAttributesFailure(state: PorAsinListState) {
      // Mark the fetch attempt as resolved so auth-gated UI can exit
      // its loading state. `editableAttributes` stays empty, which
      // downstream code treats as "user has no editable attributes".
      // Deliberately leave `isGettingEditableAttributes` untouched so
      // the several pages that use `!isGettingEditableAttributes` as
      // a no-deps "fetch once" guard don't start re-firing every
      // render on failure.
      state.editableAttributesLoaded = true;
    },
    searchPorAsinStart(
      state: PorAsinListState,
      { payload }: PayloadAction<{ queryString: string }>
    ) {
      // On a fresh filter, wipe visible rows so the table renders its
      // empty-state loading UI instead of an overlay over stale rows
      // that no longer match the applied query. Same-query refetches
      // (pagination, sort, post-save refresh) keep the rows so the
      // overlay spinner still applies.
      if (payload.queryString !== state.lastFetchedQueryString) {
        state.currentPagePorAsins = [];
        state.count = 0;
        state.error = null;
      }
      state.lastFetchedQueryString = payload.queryString;
      state.isLoading = true;
    },
    searchPorAsinSuccess(
      state: PorAsinListState,
      { payload }: PayloadAction<SearchResult>
    ) {
      state.currentPagePorAsins = payload.asins;
      state.count = payload.totalResultCount || 0;
      state.isLoading = false;
      // Clear any error left by a prior failed fetch so a same-query
      // retry (or a post-save refresh that preserved notifications)
      // doesn't leave a stale error alongside fresh rows.
      state.error = null;
      // state.restrictedPrograms = payload.restrictedPrograms
    },
    searchPorAsinFailure(
      state: PorAsinListState,
      action: PayloadAction<string>
    ) {
      state.isLoading = false;
      state.error = action.payload;
    },
    searchPorAsinDropped(
      state: PorAsinListState,
      { payload }: PayloadAction<{ queryString: string }>
    ) {
      // Release the loading flag when the thunk's out-of-order guard
      // drops a response, but only if no newer fetch has already
      // superseded this one — otherwise we'd clear the spinner while
      // the fresh request is still in flight. `lastFetchedQueryString`
      // reflects the most recent Start; if it no longer matches the
      // dropped request, a superseding fetch owns the loading state.
      if (state.lastFetchedQueryString !== payload.queryString) {
        return;
      }
      state.isLoading = false;
    },
    setEditing(state) {
      state.isEditing = true;
      state.editedRowSet = {};
      state.editablePorAsins = [...state.currentPagePorAsins];
      state.editablePorAsins.forEach((value, index) => {
        state.porIndexMap[porKey(value)] = index;
      });
    },
    setViewing(state) {
      state.isEditing = false;
      state.porIndexMap = {};
      state.edited = false;
      state.editablePorAsins = [];
    },
    updatePorAsin(state, action) {
      const [entryToUpdate, attribute, value] = action.payload;
      const updatedEntry = updateEditedEntry(
        state,
        attribute,
        value,
        entryToUpdate
      );
      state.editedRowSet[state.porIndexMap[porKey(entryToUpdate)]] = true;
      state.editablePorAsins[state.porIndexMap[porKey(entryToUpdate)]] =
        updatedEntry;
      state.edited = true;
    },
    bulkUpdatePorAsins(state, action) {
      const [attribute, value] = action.payload;
      state.editablePorAsins = state.editablePorAsins.map((entryToUpdate) => {
        if (!entryToUpdate.isRestricted) {
          state.editedRowSet[state.porIndexMap[porKey(entryToUpdate)]] = true;
          return updateEditedEntry(state, attribute, value, entryToUpdate);
        }
        return entryToUpdate;
      });
      state.edited = Object.keys(state.editedRowSet).length > 0;
    },
    setPorAttributesSuccess(state) {
      state.refresh = !state.refresh;
    },
    setPorAttributesFailure(state) {
      state.refresh = !state.refresh;
    },
  },
});

export const {
  getEditableAttributesStart,
  getEditableAttributesSuccess,
  getEditableAttributesFailure,
  searchPorAsinStart,
  searchPorAsinSuccess,
  searchPorAsinFailure,
  searchPorAsinDropped,
  setEditing,
  setViewing,
  updatePorAsin,
  bulkUpdatePorAsins,
  setPorAttributesSuccess,
  setPorAttributesFailure,
} = porAsinList.actions;

export default porAsinList.reducer;

export const fetchPorAsins =
  (
    offset: number,
    pageSize: number,
    queryString: string,
    sortFacets: SortFacet[],
    clearNotificationsBoolean: boolean = true
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (clearNotificationsBoolean) {
        dispatch(clearNotifications());
      }
      dispatch(searchPorAsinStart({ queryString }));
      const porAsinPage = await searchAsins(
        offset,
        pageSize,
        queryString,
        sortFacets
      );
      // Out-of-order guard, scoped to queryString: drop the response
      // if the user's applied query has moved on since we fired this
      // request. Without this, a slow response for a superseded query
      // can re-populate the table and re-surface the Restricted ASINs
      // flashbar over the empty state (NoFiltersPanel or the table's
      // empty slot). Also release the loading flag on the drop path
      // so a stuck spinner doesn't linger when no superseding fetch
      // is queued (e.g. user cleared filters into the zero state).
      //
      // Same-query pagination or sort races are not covered here — a
      // later page/sort with the same queryString still passes this
      // guard and the newer response can be overwritten by a slower
      // earlier one. Full request-identity ordering (offset,
      // sortFacets, page-size) would need a monotonic sequence id
      // and is left as a follow-up.
      if (getState().generateQuery.queryString !== queryString) {
        dispatch(searchPorAsinDropped({ queryString }));
        return;
      }
      dispatch(searchPorAsinSuccess(porAsinPage));
      const restrictedAsins = porAsinPage.asins.filter(
        (value) => "isRestricted" in value && value.isRestricted
      );
      if (restrictedAsins.length > 0) {
        const programNames = restrictedAsins.map(
          (value) => (value as RestrictedAsin).restrictedProgramName ?? ""
        );
        dispatch(
          addNotificationWithKey({
            key: RESTRICTED_ASINS_NOTIFICATION_KEY,
            message: RESTRICTED_SEARCH_MESSAGE_ITEMS(programNames),
          })
        );
      } else {
        // Keep the keyed flashbar aligned with the current result set.
        // On a refresh-after-save (clearNotificationsBoolean=false)
        // the previous fetch's alert would otherwise persist even
        // when the fresh page has no restricted rows.
        dispatch(removeNotification(RESTRICTED_ASINS_NOTIFICATION_KEY));
      }
    } catch (err: any) {
      if (getState().generateQuery.queryString !== queryString) {
        dispatch(searchPorAsinDropped({ queryString }));
        return;
      }
      dispatch(searchPorAsinFailure(err.toString()));
      dispatch(setNotification(ERROR_NOTIF_ITEMS(err.toString())));
    }
  };

export const setUpdatedEntries =
  (
    currentPagePorAsins: (FullAsinWithAudits | RestrictedAsin)[],
    updatedEntries: (FullAsinWithAudits | RestrictedAsin)[],
    editedRowsSet: { [index: number]: boolean }
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(clearNotifications());
      const { asinMarketplaceDates, dateTypes } =
        prepareRecordAsinMarketplaceDatesWithAsins(
          currentPagePorAsins as FullAsinWithAudits[],
          updatedEntries as FullAsinWithAudits[],
          editedRowsSet
        );
      if (asinMarketplaceDates.length !== 0) {
        const messageNotificationKeyMarketplace = moment.now();
        dispatch(
          addNotificationWithKey({
            key: messageNotificationKeyMarketplace,
            message: IN_PROGRESS_NOTIF_ITEMS("Updating POR ASINs Dates"),
          })
        );
        await recordAsinMarketplaceDatesWithAsins(
          asinMarketplaceDates,
          dateTypes
        );
        dispatch(
          addNotificationWithKey({
            key: messageNotificationKeyMarketplace,
            message:
              SET_ASIN_MARKETPLACE_DATES_SUCCESS_ITEMS(asinMarketplaceDates),
          })
        );
      }

      const asinMarketplaceAttributes = prepareSetLaunchAttributesWithAsins(
        currentPagePorAsins as FullAsinWithAudits[],
        updatedEntries as FullAsinWithAudits[],
        editedRowsSet
      );
      if (asinMarketplaceAttributes.length !== 0) {
        const messageNotificationKeyAttributes = moment.now();
        dispatch(
          addNotificationWithKey({
            key: messageNotificationKeyAttributes,
            message: IN_PROGRESS_NOTIF_ITEMS("Updating POR ASINs Attributes"),
          })
        );
        await setLaunchAttributesWithAsins(asinMarketplaceAttributes);
        dispatch(
          addNotificationWithKey({
            key: messageNotificationKeyAttributes,
            message: SET_ASIN_MARKETPLACE_ATTRIBUTES_SUCCESS_ITEMS(
              asinMarketplaceAttributes
            ),
          })
        );
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      dispatch(setNotification(ERROR_NOTIF_ITEMS(errorMessage)));
      dispatch(setPorAttributesFailure());
    }
  };

export const fetchEditableAttributes = (): AppThunk => async (dispatch) => {
  dispatch(getEditableAttributesStart());
  try {
    const attributes = await getEditableAttributes();
    if (!Array.isArray(attributes)) {
      console.error("failed to fetch editable attributes");
      dispatch(getEditableAttributesFailure());
      return;
    }
    dispatch(getEditableAttributesSuccess(attributes));
  } catch (err: unknown) {
    console.error("failed to fetch editable attributes:", err);
    dispatch(getEditableAttributesFailure());
  }
};
